import { useState } from "react";
import {
	DeleteVariable,
	UpdateVariablesDetail,
} from "../../../api/variablesController";
import { READ_ONLY_VARIABLE_NAME } from "../../../common/constants";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Icon,
	Grid,
	IconButton,
	TextField,
	Tooltip,
} from "../../../common/MaterialComponent";
import Shared from "../../../common/shared";
import { ButtonPrimary } from "../../../common/sharedStyle";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";

export interface ActionColumnProps {
	setVariablesUpdated: (flag: boolean) => void;
	tableMeta: any;
	editOnly: boolean;
	countryId: number;
}

export const ActionColumn: React.FC<ActionColumnProps> = ({
	setVariablesUpdated,
	tableMeta,
	editOnly,
	countryId,
}) => {
	const rowName = tableMeta.rowData[0];
	const rowValue = tableMeta.rowData[1];
	const rowGroup = tableMeta.rowData[2];

	const [deleteDialogOpen, setDeleteDialog] = useState(false);
	const [editDialogOpen, setEditDialog] = useState(false);
	const [editValue, setEditValue] = useState(rowValue);
	const [editGroup, setEditGroup] = useState(rowGroup);
	const [editDisabled, setEditDisabled] = useState(true);

	const handleValueChange = (event: any) => {
		const newValue = event.target.value;
		setEditValue(newValue);
		if (
			// neither the value or group change excluding added whitespace
			(newValue.trim() !== rowValue || editGroup.trim() !== rowGroup) &&
			// value is empty
			newValue.trim() !== "" &&
			// reserved group name READ_ONLY_VARIABLE_NAME is being used by non whatsapp flow variables
			(editGroup.trim() !== READ_ONLY_VARIABLE_NAME || editOnly === true)
		) {
			setEditDisabled(false);
		} else {
			setEditDisabled(true);
		}
	};

	const handleGroupChange = (event: any) => {
		const newValue = event.target.value;
		setEditGroup(newValue);
		if (
			(newValue.trim() !== rowGroup || editValue.trim() !== rowValue) &&
			editValue.trim() !== "" &&
			// don't need editOnly === true as above as can't change group if editOnly is true
			newValue.trim() !== READ_ONLY_VARIABLE_NAME
		) {
			setEditDisabled(false);
		} else {
			setEditDisabled(true);
		}
	};
	const strings = useGetComponentStringsContext().ActionColumn;

	const editVariable = async () => {
		let group;
		if (editGroup.trim() === "") {
			group = null;
		} else {
			group = editGroup.trim();
		}
		try {
			const res = await UpdateVariablesDetail({
				Name: rowName,
				Value: editValue.trim(),
				CountryId: countryId,
				Group: group,
			});
			setEditDialog(false);
			setEditDisabled(true);
			if (res.error) {
				Shared.showSnackbarMessage("Failed to update variable");
			} else {
				Shared.showSnackbarMessage("Successfully updated variable");
				setVariablesUpdated(true);
			}
		} catch (err) {
			console.error(err);
		}
	};

	const deleteVariable = async () => {
		try {
			const res = await DeleteVariable(rowName, countryId.toString());
			setDeleteDialog(false);
			if (res.error) {
				Shared.showSnackbarMessage("Failed to delete variable");
			} else {
				Shared.showSnackbarMessage("Successfully deleted variable");
				setVariablesUpdated(true);
			}
		} catch (err) {
			console.error(err);
		}
	};

	const openEditDialog = () => {
		setEditGroup(rowGroup);
		setEditValue(rowValue);
		setEditDialog(true);
	};

	return (
		<div>
			<Dialog open={deleteDialogOpen}>
				<DialogTitle>{strings.deleteDialogTitle}</DialogTitle>
				<DialogContent>
					<DialogContentText>{strings.deleteDialogText}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<ButtonPrimary onClick={() => setDeleteDialog(false)} autoFocus>
						{strings.cancelText}
					</ButtonPrimary>
					<ButtonPrimary onClick={deleteVariable}>
						{strings.deleteDialogConfirm}
					</ButtonPrimary>
				</DialogActions>
			</Dialog>

			<Dialog open={editDialogOpen}>
				<DialogTitle>{strings.editDialogTitle}</DialogTitle>
				<DialogContent>
					<Grid container>
						<Grid item xs={3}>
							<TextField
								label={strings.editDialogName}
								value={rowName}
								disabled={true}
							/>
						</Grid>
						<Grid item xs={1} />
						<Grid item xs={3}>
							<TextField
								label={strings.editDialogValue}
								value={editValue}
								onChange={handleValueChange}
								disabled={false}
								multiline
							/>
						</Grid>
						<Grid item xs={1} />
						<Grid item xs={3}>
							<TextField
								label={strings.editDialogGroup}
								value={editGroup}
								onChange={handleGroupChange}
								disabled={editOnly}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<ButtonPrimary
						onClick={() => {
							//reset values
							setEditValue(rowValue);
							setEditGroup(rowGroup);
							setEditDisabled(true);
							setEditDialog(false);
						}}
						autoFocus
					>
						{strings.cancelText}
					</ButtonPrimary>
					<ButtonPrimary disabled={editDisabled} onClick={editVariable}>
						{strings.editDialogConfirm}
					</ButtonPrimary>
				</DialogActions>
			</Dialog>

			<Tooltip title={"Edit"}>
				<IconButton className="editIcon" onClick={openEditDialog}>
					<Icon style={{ color: "blue" }}>edit</Icon>
				</IconButton>
			</Tooltip>
			{!editOnly && (
				<Tooltip title={"Delete"}>
					<IconButton className="btnIcon" onClick={() => setDeleteDialog(true)}>
						<Icon fontSize="large" className="rejectButton">
							delete
						</Icon>
					</IconButton>
				</Tooltip>
			)}
		</div>
	);
};
